import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Badge, Button, Card, Input, Table } from '@/admin-design/_common'
import { api } from '../api/client'

export default function Analyze() {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const products = useQuery({
    queryKey: ['products', search],
    queryFn: () => api.products({ q: search, limit: 20, status: 'all' }),
    enabled: true,
  })

  const analyze = useMutation({
    mutationFn: (id: number) => api.analyzeProduct(id),
  })

  const items: any[] = products.data?.items ?? []
  const result = analyze.data as any

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">상품 분석</h1>

      {/* 상품 검색 + 선택 */}
      <Card>
        <Card.Header>
          <Card.Title>상품 선택</Card.Title>
          <Card.Description>분석할 상품을 검색하고 선택하세요</Card.Description>
        </Card.Header>
        <div className="px-6 pb-4 space-y-3">
          <Input
            type="text"
            placeholder="상품명 검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          {items.length > 0 && (
            <div className="max-h-60 overflow-y-auto border border-border rounded-md">
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Header className="w-12">ID</Table.Header>
                    <Table.Header>상품명</Table.Header>
                    <Table.Header>제공업체</Table.Header>
                    <Table.Header>상태</Table.Header>
                    <Table.Header className="w-20" />
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {items.map((p: any) => (
                    <Table.Row
                      key={p.id}
                      className={selectedId === p.id ? 'bg-accent' : ''}
                    >
                      <Table.Cell className="font-mono text-xs">{p.id}</Table.Cell>
                      <Table.Cell className="text-sm truncate max-w-xs">{p.name}</Table.Cell>
                      <Table.Cell>
                        <Badge variant="muted">{p.provider}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={p.listing_status === 'synced' ? 'success' : 'muted'}>
                          {p.listing_status || 'unlisted'}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size="sm"
                          variant={selectedId === p.id ? 'primary' : 'secondary'}
                          onClick={() => {
                            setSelectedId(p.id)
                            analyze.mutate(p.id)
                          }}
                          loading={analyze.isPending && selectedId === p.id}
                        >
                          분석
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {/* 분석 에러 */}
      {analyze.isError && (
        <Card>
          <div className="p-6 text-destructive text-sm">
            분석 실패: {(analyze.error as any)?.message ?? '알 수 없는 오류'}
          </div>
        </Card>
      )}

      {/* 분석 결과 */}
      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 상품 원본 정보 */}
          <Card>
            <Card.Header>
              <Card.Title>상품 원본 (크롤 데이터)</Card.Title>
            </Card.Header>
            <div className="px-6 pb-4 space-y-2">
              {result.product.main_image && (
                <img
                  src={result.product.main_image}
                  alt=""
                  className="w-24 h-24 object-cover rounded border border-border"
                />
              )}
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium text-muted-foreground w-24 shrink-0">원본 링크</span>
                <a
                  href={providerURL(result.product.provider, result.product.provider_product_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  {result.product.provider} 상품 페이지 &rarr;
                </a>
              </div>
              <FieldRow label="상품명" value={result.product.name} />
              <FieldRow label="정제 상품명" value={result.product.clean_name} />
              <FieldRow label="브랜드 (크롤)" value={result.product.brand || '-'} />
              <FieldRow label="제공업체" value={result.product.provider} />
              <FieldRow label="도매가" value={`${result.product.price?.toLocaleString()}원`} />
              <FieldRow label="노출가" value={`${result.product.expose_price?.toLocaleString()}원`} />
              <FieldRow label="판매가 (계산)" value={`${result.product.sale_price?.toLocaleString()}원`} />
              <FieldRow label="판매자코드" value={result.product.smc} mono />
            </div>
          </Card>

          {/* LLM 분석 결과 */}
          <Card>
            <Card.Header>
              <Card.Title>LLM 분석 결과</Card.Title>
              <Card.Description>
                토큰: prompt={result.llm.usage.prompt_tokens} completion={result.llm.usage.completion_tokens} total={result.llm.usage.total_tokens}
              </Card.Description>
            </Card.Header>
            <div className="px-6 pb-4 space-y-2">
              <FieldRow label="브랜드" value={result.llm.brand || '-'} highlight={!!result.llm.brand} />
              <FieldRow label="제조사" value={result.llm.manufacturer || '-'} highlight={!!result.llm.manufacturer} />
              <FieldRow label="모델명" value={result.llm.model_name || '-'} />
              <FieldRow label="원산지" value={result.llm.origin || '-'} />
              <FieldRow label="제조일자" value={result.llm.manufacture_date || '-'} />
              <FieldRow label="유효기한" value={result.llm.valid_date || '-'} />
              <div className="pt-1">
                <span className="text-xs font-medium text-muted-foreground">태그 (원본)</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {result.llm.tags?.map((t: string, i: number) => (
                    <Badge key={i} variant="info">{t}</Badge>
                  )) ?? <span className="text-xs text-muted-foreground">-</span>}
                </div>
              </div>
              <div className="pt-1">
                <span className="text-xs font-medium text-muted-foreground">태그 (sanitized)</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {result.llm.tags_sanitized?.map((t: string, i: number) => (
                    <Badge key={i} variant="success">{t}</Badge>
                  )) ?? <span className="text-xs text-muted-foreground">-</span>}
                </div>
              </div>
            </div>
          </Card>

          {/* 고시정보 */}
          <Card>
            <Card.Header>
              <Card.Title>상품정보제공고시 (LLM)</Card.Title>
            </Card.Header>
            <div className="px-6 pb-4 space-y-2">
              <FieldRow label="제품명" value={result.llm.notice?.product_name || '-'} />
              <FieldRow label="유통기한" value={result.llm.notice?.expiration_date || '-'} />
              <FieldRow label="소비기한" value={result.llm.notice?.consumption_date || '-'} />
              <FieldRow label="보관방법" value={result.llm.notice?.storage_method || '-'} />
              <FieldRow label="포장단위" value={result.llm.notice?.package_amount || '-'} />
              <FieldRow label="원재료" value={result.llm.notice?.ingredients || '-'} />
              <FieldRow label="영양성분" value={result.llm.notice?.nutrition_facts || '-'} />
              <FieldRow label="주의사항" value={result.llm.notice?.caution || '-'} />
            </div>
          </Card>

          {/* 네이버 등록 프리뷰 */}
          <Card>
            <Card.Header>
              <Card.Title>네이버 등록 프리뷰</Card.Title>
              <Card.Description>실제 등록 시 사용될 값</Card.Description>
            </Card.Header>
            <div className="px-6 pb-4 space-y-2">
              <FieldRow label="카테고리" value={`${result.naver_preview.category_name} (${result.naver_preview.category_id})`} />
              <FieldRow label="판매가" value={`${result.naver_preview.sale_price?.toLocaleString()}원`} />
              <FieldRow label="브랜드" value={result.naver_preview.brand || '-'} highlight={!!result.naver_preview.brand} />
              <FieldRow label="제조사" value={result.naver_preview.manufacturer || '-'} highlight={!!result.naver_preview.manufacturer} />
              <FieldRow label="모델명" value={result.naver_preview.model_name || '-'} />
              <FieldRow label="판매자코드" value={result.naver_preview.smc} mono />
              <div className="pt-1">
                <span className="text-xs font-medium text-muted-foreground">태그</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {result.naver_preview.tags?.map((t: string, i: number) => (
                    <Badge key={i} variant="success">{t}</Badge>
                  )) ?? <span className="text-xs text-muted-foreground">-</span>}
                </div>
              </div>
            </div>
          </Card>

          {/* LLM Raw Response */}
          <Card className="lg:col-span-2">
            <Card.Header>
              <Card.Title>LLM Raw Response</Card.Title>
            </Card.Header>
            <div className="px-6 pb-4">
              <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap">
                {result.llm.raw_response}
              </pre>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

function FieldRow({
  label,
  value,
  mono,
  highlight,
}: {
  label: string
  value: string
  mono?: boolean
  highlight?: boolean
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs font-medium text-muted-foreground w-24 shrink-0">{label}</span>
      <span
        className={[
          'text-sm break-all',
          mono ? 'font-mono' : '',
          highlight ? 'text-primary font-medium' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </span>
    </div>
  )
}

function providerURL(provider: string, pid: string): string {
  switch (provider) {
    case 'wsmon':
      return `https://www.wsmon.co.kr/shop/item.php?it_id=${pid}`
    case 'upickb2b':
      return `https://upickb2b.com/product/detail.html?product_no=${pid}`
    default:
      return '#'
  }
}
